import type { TemplateContext } from "@unbox/api";
import { AlertTriangle } from "@untitledui/icons";
import { useEffect, useState } from "react";
import { cx } from "@/utils/cx";
import { resolveForPreview } from "@/utils/message-content";
import type { ComposerImage } from "./composer-image";

const SAMPLE_VIDEO_LINK = "https://www.tiktok.com/@mayachen/video/0000000000000000000";

export const SAMPLE_PREVIEW_CONTEXT: TemplateContext = {
  creatorName: "Maya Chen",
  shopName: "Northlight Skincare",
  commissionValue: "15%",
  videoUrls: [SAMPLE_VIDEO_LINK],
  creatorTikTokVideoLinks: [SAMPLE_VIDEO_LINK],
  exampleVideos: [SAMPLE_VIDEO_LINK],
  productNameList: ["Barrier Repair Serum"],
  productDescription: "A lightweight serum that calms and repairs the skin barrier.",
  flagTypes: ["Misleading health claim"],
  flagExplanations: ["The video claims the product cures eczema."],
  creatorRiskExplanations: ["The video claims the product cures eczema."],
};

const PREVIEW_EXIT_MS = 200;

export function usePreviewPresence(open: boolean) {
  const [mounted, setMounted] = useState(open);
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }
    if (!mounted) return;
    const timer = setTimeout(() => setMounted(false), PREVIEW_EXIT_MS);
    return () => clearTimeout(timer);
  }, [open, mounted]);
  return { mounted, closing: mounted && !open };
}

type ComposerPreviewProps = {
  message: string;
  image?: ComposerImage | null;
  context: TemplateContext;
  closing?: boolean;
  minHeight?: number;
  className?: string;
};

export function ComposerPreview({ message, image, context, closing, minHeight, className }: ComposerPreviewProps) {
  const trimmed = message.trim();
  return (
    <div
      aria-live="polite"
      style={{ minHeight }}
      className={cx(
        "flex flex-col overflow-hidden rounded-[11px] bg-unbox-cream-25",
        closing ? "motion-safe:animate-sink-out pointer-events-none" : "motion-safe:animate-rise-in",
        className,
      )}
    >
      <div className="flex flex-1 flex-col gap-2.5 p-4">
        <span className="text-xs font-semibold tracking-[0.06em] text-quaternary uppercase">Preview</span>
        {image && (
          <img
            src={image.previewUrl}
            alt={image.name}
            className="max-h-48 w-full rounded-lg border border-secondary bg-primary object-cover"
          />
        )}
        {trimmed && (
          <p className="text-[15px] leading-7 break-words whitespace-pre-wrap text-primary">
            {resolveForPreview(trimmed, context)}
          </p>
        )}
      </div>
      <p className="flex items-center gap-1.5 bg-unbox-cream-50 px-4 py-2.5 text-xs leading-5 text-tertiary">
        <AlertTriangle aria-hidden className="size-3.5 shrink-0 text-fg-warning-primary" />
        <span>Preview the information you are sending.</span>
      </p>
    </div>
  );
}
